import Supercluster from "supercluster"
import { BIKE_MARKER_COLORS } from "../static/bike"
import { Coord } from "./map"
import { AlertEntity, AlertBikeEntity, AlertZoneEntity } from "../types/alert"
import { MapBikeEntity } from "../hooks/queries/useGetMapBikesQuery"

export const CLUSTER_MARKER_INNER_RADIUS = 15

export const CLUSTER_MARKER_OUTER_RADIUS = 20

export const CLUSTER_MARKER_PIE_SIZE = 40

export const SUPERCLUSTER_OPTIONS = {
  radius: 80,
  minZoom: 0,
  maxZoom: 19,
  nodeSize: 64,
  log: false,
}

export const getStateModeColor = (stateCounts: { [key: string]: number }, theme: any): string => {
  // This gets the state with the most occurences so we can use as the primary background color of cluster
  const max = Math.max(...Object.values(stateCounts))
  const mode = Object.entries(stateCounts).find(([_, count]) => count === max)
  if (mode) return theme?.colors?.[BIKE_MARKER_COLORS[mode[0]]]["600"]
  return "blue.600"
}

export interface BikesCluster {
  type: string
  id?: number
  properties: {
    cluster?: boolean
    cluster_id?: number
    point_count?: number
    point_count_abbreviated?: number
    bicycle?: MapBikeEntity
    color?: string
  }
  geometry: {
    type: "Point"
    coordinates: Coord
  }
}

export interface AlertsCluster {
  type: string
  id?: number
  properties: {
    cluster?: boolean
    cluster_id?: number
    point_count?: number
    point_count_abbreviated?: number
    bicycle?: AlertBikeEntity
    zone?: AlertZoneEntity
    alerts?: AlertEntity[]
    state?: string
  }
  geometry: {
    type: "Point"
    coordinates: Coord
  }
}

type CalcClusterStatePercentageResult = {
  uuids: string[]
  stateCounts: { [key: string]: number }
}

type CalcClusterStatePercentageArgs = {
  currentCluster: BikesCluster | AlertsCluster
  superClusterInstance: Supercluster | undefined
  isAlertCluster?: boolean
}

export const calcClusterStatePercentage = ({
  currentCluster,
  superClusterInstance,
  isAlertCluster,
}: CalcClusterStatePercentageArgs): CalcClusterStatePercentageResult => {
  try {
    if (!currentCluster?.properties.cluster || !currentCluster?.properties?.cluster_id) {
      return { uuids: [], stateCounts: {} }
    }
    const leaves = superClusterInstance?.getLeaves(currentCluster.properties.cluster_id)
    const states =
      leaves?.map((leaf) => {
        // map for alerts clusters
        if (isAlertCluster) {
          return leaf.properties.bicycle.bicycle_state ?? "undefined"
        }
        // map for bikes clusters
        return leaf.properties.bicycle.state
      }) ?? []
    const uuids =
      leaves?.map((leaf) => {
        // map for alerts clusters
        if (isAlertCluster) {
          return leaf.properties.bicycle.bicycle_uuid ?? leaf.properties.zone.zone_uuid
        }
        // map for bikes clusters
        return leaf.properties.bicycle.uuid
      }) ?? []
    const stateCounts = states?.reduce((acc, curr) => {
      acc[curr] = acc[curr] ? acc[curr] + 1 : 1
      return acc
    }, {})
    return { uuids, stateCounts }
  } catch {
    return { uuids: [], stateCounts: {} }
  }
}
